import type * as pf from '@amirada/lib-shared'
import { PfCmdType } from '@amirada/lib-shared'
import type { IPriceListNode, IPriceListPdf } from '@amirada/lib-wilo-shared'
import { usePfinderAPI as useBasePfinderAPI } from '@base/composables/pfinderAPI'
import { useAppIndexStore } from '@base/stores/indexStore'
import { useMaskStore } from '@base/stores/maskStore'
import { ut_getFilterDescr, ut_getFilterNodes } from '@base/utils/utils'
import type { ECarts } from '@base/utils/constants'
import type { ECartContentType } from '~/utils/constants'
import { ECartUserDefined, def_priceListContentType, def_priceListSeriesTypes } from '~/utils/constants'
import type { IPdfSettings, IPriceTable } from '~/utils/types'
import { ut_wilo_newContentItem, ut_wilo_priceListMaskParam, ut_wilo_priceTableQuery, ut_wilo_psxFromItems } from '~/utils/priceList'
import { usePriceListCart } from '~/composables/priceListCart'

/*
 * Taking a Wilo template over into the price-list cart (moved out of CartManage, 14.10.2026 — the
 * project management stays the screen, this is the logic behind its template tiles).
 *
 * `takeOver` resolves the template's series against what exists in this country today, drops what is
 * unavailable or unknown, and appends the rest — structure and series, in export order — to the cart.
 * A series with a price-list filter template (`filterProps`) comes with its price tables already
 * configured, one per value combination (14.10.2026, see priceTablesFor).
 * `pdfDefaults` is what the new project's pdf settings start with. Opening the project, the lock and
 * the name stay with the caller.
 */

// TEST SWITCH — with the data in place this belongs on false.
// Turned on, every series stays regardless of its namePath. Without price-list data nothing comes
// back from $names:, so every series counts as unavailable and gets dropped — and with it every
// chapter above it, which leaves nothing to hand over at all.
const def_keepUnresolvedSeries: boolean = false

export function usePriceListTemplate(cart: ECarts) {
  const basePfinderAPI = useBasePfinderAPI()
  const indexStore = useAppIndexStore()
  const priceListRows = usePriceListCart(cart)

  /*
   * Put the picked template into the cart (appended to what is there — the caller empties it first).
   * Answers whether anything survived: false means not one series of this template is available here.
   */
  async function takeOver(picked: IPriceListNode): Promise<boolean> {
    // The loading overlay of the app for the whole takeover: with the masks of the series that get
    // their price tables it is no longer a moment (counted loading state, see indexStore.withLoading).
    return indexStore.withLoading(async () => {
      const byNodeName: Record<string, string> = await resolveNamePaths(collectSeries(picked))
      const selection: IPriceListNode[] = (resolveNode(picked, byNodeName).children || [])
        .map(prune)
        .filter((child): child is IPriceListNode => !!child)
      if (!selection.length) { return false }
      await addSelectionToCart(selection)
      return true
    })
  }

  /*
   * The pdf settings a project from this template starts with: the catalog's cover title, subtitle and
   * footer in every language CMI has, and its cover image as the preselected cover. Fields the catalog
   * does not maintain stay out, so the modal keeps its own default for them.
   */
  function pdfDefaults(pdf: IPriceListPdf | undefined): IPdfSettings | undefined {
    if (!pdf) { return undefined }
    const res: IPdfSettings = {}
    if (pdf.title) { res.titleTexts = { ...pdf.title } }
    if (pdf.subtitle) { res.subtitleTexts = { ...pdf.subtitle } }
    if (pdf.footer) { res.disclaimerTexts = { ...pdf.footer } }
    if (pdf.cover) {
      res.coverPicTemplate = pdf.cover
      res.coverPicSelected = pdf.cover
    }
    return res
  }

  // A series or accessory — NOT "has no children": the export deliberately keeps empty groups, and
  // from the outside those look exactly like a series. Only these two type codes are cart positions;
  // the known structure codes are markers, unknown ones are ignored (isKnownNode).
  function isSeriesNode(node: IPriceListNode): boolean {
    return def_priceListSeriesTypes.includes(node.typeCod)
  }

  // A code this client knows: a series, or a structure level listed in def_priceListContentType.
  // ⚑ Everything else is IGNORED together with its whole subtree (decision 18.09.2026) — no fallback
  // marker. A code the CMI adds later shows up in no price list until it is entered here.
  function isKnownNode(node: IPriceListNode): boolean {
    return isSeriesNode(node) || !!def_priceListContentType[node.typeCod]
  }

  // Unknown subtrees are skipped here already, so their series are not even looked up.
  function collectSeries(node: IPriceListNode, out: string[] = []): string[] {
    if (isSeriesNode(node)) { out.push(node.nodeName) }
    ;(node.children || []).filter(isKnownNode).forEach(child => collectSeries(child, out))
    return out
  }

  // One request for the whole template. The OID has to be recovered from the path because
  // SearchResultItem carries no nodeName — same trick as expSearch on the server side.
  async function resolveNamePaths(nodeNames: string[]): Promise<Record<string, string>> {
    const byNodeName: Record<string, string> = {}
    if (!nodeNames.length) { return byNodeName }
    const args: pf.PfCmd<PfCmdType.SEARCH>['arg'] = {
      term: '$names:' + nodeNames.join(','),
      limit: nodeNames.length,
      sqlLimit: 0,
    }
    // date=now overrides the window pfinder-wilo puts on EVERY search: allowOldButNotFutureUnlessDated()
    // sets 'now|1900', which deliberately keeps discontinued products findable and only drops
    // not-yet-active ones. A price list must not offer those — a series that is no longer valid today
    // has no business in it. `date` is the documented master window and switches the check back to
    // "active now"; an explicit date from the URL still beats it, since fetchData copies
    // queryUrlServer over paramsQuery afterwards.
    const res = await basePfinderAPI.fetchData(PfCmdType.SEARCH, args, { paramsQuery: { date: 'now' } })
    for (const item of res?.result?.found || []) {
      const namePath: string | undefined = item.namePaths?.[0]
      const nodeName: string | undefined = namePath?.split('/').pop()
      if (namePath && nodeName) { byNodeName[nodeName] = namePath }
    }
    return byNodeName
  }

  // A copy of the template with the namePaths filled in, the unavailable series dropped and the
  // unknown codes (isKnownNode) dropped with everything below them.
  function resolveNode(node: IPriceListNode, byNodeName: Record<string, string>): IPriceListNode {
    const out: IPriceListNode = { nodeName: node.nodeName, typeCod: node.typeCod, desc: node.desc, descs: node.descs }
    if (isSeriesNode(node)) {
      out.namePath = byNodeName[node.nodeName]
      if (node.filterProps?.length) { out.filterProps = node.filterProps }
      return out
    }
    const children: IPriceListNode[] = (node.children || [])
      .filter(isKnownNode)
      .filter(child => def_keepUnresolvedSeries || !isSeriesNode(child) || !!byNodeName[child.nodeName])
      .map(child => resolveNode(child, byNodeName))
    if (children.length) { out.children = children }
    return out
  }

  // Throws away what leads nowhere in this country: a structure node comes along only when a series is
  // left underneath it — otherwise the list would start with chapter headings that have no content,
  // and those would travel into the PDF's table of contents as well.
  function prune(node: IPriceListNode): IPriceListNode | undefined {
    if (isSeriesNode(node)) {
      if (!node.namePath && !def_keepUnresolvedSeries) { return undefined }
      const series: IPriceListNode = { nodeName: node.nodeName, typeCod: node.typeCod, desc: node.desc, descs: node.descs }
      if (node.namePath) { series.namePath = node.namePath }
      if (node.filterProps?.length) { series.filterProps = node.filterProps }
      return series
    }

    const children: IPriceListNode[] = (node.children || [])
      .map(prune)
      .filter((child): child is IPriceListNode => !!child)
    if (!children.length) { return undefined }

    return { nodeName: node.nodeName, typeCod: node.typeCod, desc: node.desc, descs: node.descs, children }
  }

  /*
   * The whole selection travels as ONE list, markers included, through priceListRows.insertRows: it
   * resolves the series in a single request, never sends the local markers and keeps everything in its
   * original order. Appended, without a position.
   *
   * ⚑ Not setAddListToCart (24.09.2026): that one skips a namePath already in the cart, and a template
   * may hold the same series under two chapters — two rows, both kept (see composables/priceListCart).
   */
  async function addSelectionToCart(selection: IPriceListNode[]): Promise<void> {
    const items: pf.CartItem[] = []
    // The series rows that get price tables, with the template properties they come from.
    const withFilters: { item: pf.CartItem, filterProps: string[] }[] = []

    function collect(node: IPriceListNode): void {
      if (isSeriesNode(node)) {
        // The namePath is all that is needed here — insertRows resolves the rest.
        if (node.namePath) {
          const item: pf.CartItem = { namePath: node.namePath }
          items.push(item)
          if (node.filterProps?.length) { withFilters.push({ item, filterProps: node.filterProps }) }
        }
      }
      else {
        // Everything that is not a series is structure of a KNOWN level: resolveNode has already
        // dropped the unknown codes with their subtrees. The guard only keeps the type honest.
        const type: ECartContentType | undefined = def_priceListContentType[node.typeCod]
        if (!type) { return }
        // EVERY language of the template, not the one on screen: a project has to be able to switch
        // language afterwards. `label` keeps the english text as the last resort.
        items.push(ut_wilo_newContentItem(type, node.desc, { ...node.descs }))
      }
      ;(node.children || []).forEach(collect)
    }

    selection.forEach(collect)

    // The price tables travel on the row itself (userDefined), so insertRows keeps them like every other
    // userDefined field. One computation per series — a series under two chapters gets the same tables.
    const tablesBySeries = new Map<string, IPriceTable[]>()
    for (const { item, filterProps } of withFilters) {
      let tables: IPriceTable[] | undefined = tablesBySeries.get(item.namePath)
      if (!tables) {
        tables = await priceTablesFor(item.namePath, filterProps)
        tablesBySeries.set(item.namePath, tables)
      }
      if (tables.length) {
        item.userDefined = { ...(item.userDefined || {}), [ECartUserDefined.cart_us_priceTable]: tables.map(table => ({ ...table })) }
      }
    }

    await priceListRows.insertRows(items)
  }

  /*
   * The price tables of a series with a price-list filter template (14.10.2026, Christoph): ONE table per
   * combination of the values of the template properties — the first property is the outer loop, so
   * flange DN 32 x seal A, DN 32 x seal B, …, then DN 40 x …. No upper limit, and no unfiltered table.
   *
   * The export only names the properties; the psx belongs to the mask of THIS series in THIS market, so
   * it is read here, exactly as the table editor reads it (ut_wilo_priceListMaskParam, same store entry).
   * A filter is found by the property it carries (`props`), not by taking its name apart. A property
   * that is no filter of this series here is left out; the values the mask offers are the ones with hits
   * (the server drops the rest). A range filter (slider) has no values to enumerate and is left out too.
   *
   * The filter texts are written right away in the language of the app, so the chips show at once; the
   * refresh of the table rewrites them on every mount anyway (CartSeriesPriceTable).
   */
  async function priceTablesFor(namePath: string, filterProps: string[]): Promise<IPriceTable[]> {
    const maskData = await useMaskStore().callMaskData(ut_wilo_priceListMaskParam(namePath, '', ''))
    const filterNodes: pf.TPfNode[] = maskData ? ut_getFilterNodes(maskData) : []
    const dimensions: { item: string, desc: string }[][] = []
    for (const prop of filterProps) {
      const filter: pf.TPfNode | undefined = filterNodes.find(node => (node.props || []).some(propNode => propNode.nodeName === prop))
      if (!filter?.values?.length || filter.attrs?.SGO === '4') { continue }
      dimensions.push(filter.values.map((value) => {
        const item: string = filter.nodeName + '|' + value.GUID
        return { item, desc: ut_getFilterDescr(item, filterNodes, true) }
      }))
    }
    if (!dimensions.length) { return [] }

    let combinations: { item: string, desc: string }[][] = [[]]
    for (const dimension of dimensions) {
      combinations = combinations.flatMap(combination => dimension.map(value => [...combination, value]))
    }
    return combinations.map(combination => ({
      query: ut_wilo_priceTableQuery(ut_wilo_psxFromItems(combination.map(value => value.item)), '', ''),
      filtersDesc: combination.map(value => value.desc),
    }))
  }

  return { takeOver, pdfDefaults }
}
